import React, {useEffect, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {TransactionDetailsResponse} from "../../api/TransactionApi";
import NotificationDetailsDialog from "../NotificationDetailsDialog/NotificationDetailsDialog";
import styles from './TransactionDetailsDialog.module.css';
import ListTableSection from "../ListTableSection/ListTableSection";
import imgNoExport from "../../images/notExported.png";
import imgSuccess from "../../images/exportSuccessful.png";
import imgFailed from "../../images/exportError.png";

interface TransactionDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    details: TransactionDetailsResponse | null;
}

export const TransactionDetailsDialog: React.FC<TransactionDetailsDialogProps> = ({
                                                                                      open,
                                                                                      onClose,
                                                                                      details,
                                                                                  }) => {
    const [notificationId, setNotificationId] = useState<string | null>(null);
    const [notificationOpen, setNotificationOpen] = useState<boolean>(false);

    const handleOpenNotification = (id: string) => {
        setNotificationId(id);
        setNotificationOpen(true);
    };

    const handleCloseNotification = () => {
        setNotificationOpen(false);
        setNotificationId(null);
    };


    useEffect(() => {
        if (!open) {
            setNotificationOpen(false);
            setNotificationId(null);
        }
    }, [open]);

    const isSuccessStatus = (s?: string) => {
        if (!s) return false;
        return s.toLowerCase().includes("successfully");
    };

    const renderExportImage = (exportStatus?: string, altPrefix = "Export") => {
        if (!exportStatus) {
            return <img src={imgNoExport} alt={`${altPrefix} - No export`}/>;
        }
        if (isSuccessStatus(exportStatus)) {
            return <img src={imgSuccess} alt={`${altPrefix} - Success`}/>;
        }
        return <img src={imgFailed} alt={`${altPrefix} - Not successful`}/>;
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Transaction Details</DialogTitle>
                <DialogContent dividers>
                    {details ? (
                        <Box>
                            {details.transactionId && <p><strong>Transaction ID:</strong> {details.transactionId}</p>}
                            {details.totalAmount && details.transactionCurrency && (
                                <p><strong>Total amount:</strong> {details.totalAmount} {details.transactionCurrency}</p>
                            )}
                            {details.transactionDate &&
											 <p>
												 <strong>Date:</strong> {details.transactionDate}
															</p>}
                            {details.merchantId && <p><strong>Merchant:</strong> {details.merchantId}</p>}
                            {details.status && <p><strong>Status:</strong> {details.status}</p>}
                            {details.processingStatus && <p><strong>Processing status:</strong> {details.processingStatus}</p>}
                            {details.isPartiallyChargeable !== undefined && (
                                <p><strong>Partially chargeable:</strong> {details.isPartiallyChargeable ? "Yes" : "No"}</p>
                            )}
                            <ListTableSection
                                title="Notification Logs"
                                headers={["PSP Reference", "Amount", "Date", "Action"]}
                                rows={details.notificationLogs}
                                rowKey={n => n.uuid}
                                renderRow={n => (
                                    <>
                                        <td>{n.pspReference}</td>
                                        <td>{n.amountValue}</td>
                                        <td>{n.eventDate}</td>
                                        <td>
                                            <button className={styles.button} type="button"
                                                    onClick={() => handleOpenNotification(n.uuid)}>
                                                View
                                            </button>
                                        </td>
                                    </>
                                )}
                            />
                            <ListTableSection
                                title="Authorizations"
                                headers={["PSP Reference", "Amount", "Date"]}
                                rows={details.authorizations}
                                rowKey={a => a.authorizationId}
                                renderRow={a => (
                                    <>
                                        <td>{a.pspReference}</td>
                                        <td>{a.authorizationAmount}</td>
                                        <td>{a.authorizationDate}</td>
                                    </>
                                )}
                            />
                            <ListTableSection
                                title="Captures"
                                headers={["PSP Reference", "Amount", "Date", "Export Status"]}
                                rows={details.captures}
                                rowKey={c => c.captureId}
                                renderRow={c => (
                                    <>
                                        <td>{c.pspReference}</td>
                                        <td>{c.capturedAmount}</td>
                                        <td>{c.captureDate}</td>
                                        <td>{renderExportImage(c.exportStatus, "Capture")}</td>
                                    </>
                                )}
                            />
                            <ListTableSection
                                title="Refunds"
                                headers={["PSP Reference", "Amount", "Date", "Export Status"]}
                                rows={details.refunds}
                                rowKey={r => r.refundId}
                                renderRow={r => (
                                    <>
                                        <td>{r.pspReference}</td>
                                        <td>{r.refundedAmount}</td>
                                        <td>{r.refundDate}</td>
                                        <td>{renderExportImage(r.exportStatus, "Refund")}</td>
                                    </>
                                )}
                            />
                        </Box>
                    ) : (
                        <p>Loading...</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            <NotificationDetailsDialog
                open={notificationOpen}
                notificationId={notificationId}
                onClose={handleCloseNotification}
            />
        </>
    );
};